/**
 * Every reason the ticket's primary button is disabled, and the sentence that
 * says so.
 *
 * The plan's exit criterion is **"a disabled button with a reason, never a
 * surprise rejection"**, and this file is that criterion: one pure function,
 * every branch derived from data the client already holds, every string from
 * `docs/07` §1 verbatim with its numbers interpolated from `GET /config`.
 *
 * Two rules govern what may go in here.
 *
 * **Nothing pre-empts the engine's own gate.** Acceptance is
 * `server_receive_time < expiry_at_ms`, evaluated at the sequencer, never at the
 * edge (§7.3 step 4, D6). The last second of a market is legitimately tradeable
 * and is the second users care about most, so this function does **not** disable
 * early "to be safe" — a client that stops accepting at `T+59` has invented a
 * freeze, which is a banned concept rather than a caution. The only clock-shaped
 * blocks below are the two the spec asks for by name: the final `taker_delay_ms`
 * for a *market* order, which genuinely cannot clear (§7.4), and the final 500 ms
 * for a client whose measured clock offset exceeds 250 ms (D9).
 *
 * **Every branch has a caller.** §F21m is the reason this is worth writing down:
 * `mayAutoRotate()` shipped with twenty passing tests and no caller, so a
 * specified, implemented, verified feature was dead. This function is exactly
 * that shape. `order-validation.test.ts` asserts every `GateCode` is reachable,
 * and `<OrderTicket>` renders whatever comes back rather than re-deciding any of
 * it.
 *
 * No React, no DOM, no `Date.now()` — the clock arrives as `nowMs` from
 * `lib/clock` (invariants 7 and 10).
 */

import { formatFixed, SHARES_DP, USDC_DP, type Outcome } from '@/lib/decimal'
import { formatMmSs, formatShares, formatTtl, formatUsdc } from '@/lib/format'
import type { MarketState } from './lifecycle'
import type { TradingLimits } from './order-config'
import type { Quote } from './quote'
import type { TicketGate } from '@/wallet/identity'
import type { SetupStep } from './trading-setup'

export type GateCode =
  | 'READY'
  // Identity — `ticketGate()` decides the precedence, here and in the header.
  | 'CONNECT'
  | 'SIGN_IN'
  | 'WRONG_ACCOUNT'
  | 'WRONG_CHAIN'
  | 'ENABLE_TRADING'
  // Account and jurisdiction.
  | 'GEO_BLOCKED'
  | 'ACCOUNT_RESTRICTED'
  // Venue kill switch (`config.status.mode`).
  | 'VENUE_POST_ONLY'
  | 'VENUE_CANCEL_ONLY'
  | 'VENUE_HALTED'
  | 'VENUE_MAINTENANCE'
  // Transport.
  | 'SOCKET_STALE'
  // The venue's signing configuration is one this client will not sign against
  // (§F48e). Above the transport codes in precedence, because a stale socket is
  // a wait and this is a refusal: no amount of reconnecting fixes it.
  | 'SIGNING_UNTRUSTED'
  | 'CLOCK_RESYNC'
  | 'RATE_LIMITED'
  // Market lifecycle.
  | 'MARKET_SCHEDULED'
  | 'MARKET_FIXING'
  | 'MARKET_RESOLVING'
  | 'TAKER_DELAY_NO_TIME'
  // Order shape.
  | 'SIZE_EMPTY'
  | 'SIZE_BELOW_MIN'
  | 'SIZE_ABOVE_MAX'
  | 'SIZE_OFF_STEP'
  | 'PRICE_TICK'
  | 'INSUFFICIENT_LIQUIDITY'
  // Risk.
  | 'INSUFFICIENT_BALANCE'
  | 'POSITION_CAP'
  | 'MARKET_OI_CAP'
  | 'MAX_OPEN_ORDERS'
  // Not a block: a market order that will fill only part of its size.
  | 'PARTIAL_FILL'

/** The inline affordance a helper line offers. `docs/07` §1 specifies two. */
export type GateAction =
  | { kind: 'USE_MAX'; sizeShares: bigint }
  | { kind: 'SUPPORT' }

export interface OrderGate {
  code: GateCode
  blocked: boolean
  /**
   * The button's label, or `null` to keep the default `BUY UP ▲`.
   *
   * `null` is not an oversight — `docs/07` §1 disables the button *without*
   * changing its label for the size and collar rows, because the fault is in the
   * field the user is editing and relabelling the button points away from it.
   */
  label: string | null
  helper: string | null
  action?: GateAction
}

export interface OrderGateInput {
  /** From `ticketGate()`. The header and the ticket must not disagree. */
  gate: TicketGate
  /** `session.account_status`. `null` before a session exists. */
  accountStatus: string | null
  /** `session.screening_status !== 'CLEAR'`, or a `GEO_BLOCKED` already seen. */
  geoBlocked: boolean
  /**
   * `config.session_keys.key_ttl_ms` (`Runtime.sessionKeyTtlMs`), for the
   * `ENABLE TRADING` helper. Keys live as long as the venue says (docs/06
   * §F75), so the sentence reads the number rather than carrying one.
   */
  sessionKeyTtlMs: number

  limits: TradingLimits

  marketState: MarketState
  nowMs: number
  openAtMs: number
  expiryAtMs: number
  // `nextOpenAtMs` was here until register §F24, threaded from `<TradeRoute>`
  // through `<OrderTicket>` to drive the `RESOLVING` helper's "the next market
  // opens in {n}s". The successor now opens in the instant this market expires,
  // so there is nothing to count toward and the string is gone (`docs/07` §4).
  // Removed rather than left unread: a parameter three files carry and nobody
  // consumes is the §F21m shape this repository greps for at the end of a phase.

  /**
   * The socket is not `OPEN`.
   *
   * **At 0 ms, not after two seconds** — `docs/03` §9.2.1 is explicit that order
   * entry disables immediately, because trading off a book you know you are not
   * receiving updates for is the one thing worse than not trading. The two
   * seconds belong to the *banner*, which is a different surface with a
   * different job (`docs/07` §3, and its §1 row is corrected by §F29b).
   */
  socketStale: boolean
  /**
   * `RuntimeState.signingBlocked` — the venue published an EIP-712 domain this
   * client will not sign against, or `null`.
   *
   * `null` is also "not checked yet", which is correct: nothing is being signed
   * before `ensureIdentity()` either. `SessionKeyManager` refuses independently
   * at the two calls that produce a signature, so this is the *explanation*
   * rather than the enforcement (§F48e).
   */
  signingBlocked: { code: string; detail: string } | null
  /**
   * The heartbeat's `venue_status` is `MAINTENANCE` — a release has the venue
   * down for a minute, or a venue-core is restarting (`docs/06` §F118, §F132).
   * Every order would be refused, so the ticket says so before the click rather
   * than after it. Omitted is `false`. Distinct from `limits.mode`, which is
   * `/config`'s kill switch and keeps its own precedence (§F29c).
   */
  venueMaintenance?: boolean
  /**
   * What the venue's last response said was left on the order bucket, and when
   * it resets (§9.2.10). `null` means it has never said, which is "nothing to
   * predict" and never "you may not trade".
   */
  rateAllowance: { remaining: number; resetAtMs: number | null } | null
  /** `|offset|` from `lib/clock`, or `null` while unsynced. */
  clockOffsetMs: number | null

  orderType: 'LIMIT' | 'MARKET'
  outcome: Outcome
  /** Micro-shares. */
  sizeShares: bigint
  /** The user's limit in their own outcome's terms; `null` for a market order. */
  limitPriceCents: number | null

  quote: Quote

  /** `balances.available` in micro-USDC, or `null` before the snapshot lands. */
  availableMicroUsdc: bigint | null
  /**
   * Micro-shares already held **of the outcome this order would grow**, in this
   * market.
   *
   * For a BUY that is the outcome the order names. For a SELL it is the
   * *complement*: selling UP you do not hold mints DOWN (§7.8), so the position
   * cap this order can breach is DOWN's. Right for BUY and wrong for SELL is
   * B3.5's own shape, and it is the one this field is spelled out to avoid.
   */
  positionShares: bigint
  /**
   * Micro-shares of this order that are a **close** rather than a mint — `0n`
   * for every BUY, and for a SELL the covered part of
   * `domain/sell-shape.ts`'s split.
   *
   * `docs/04` §7.7: "SELL UP `n` while holding ≥ `n` UP: **0** — it is a close,
   * the shares are the collateral." So the covered part locks nothing, grows no
   * position and creates no open interest, and every risk row below is scored
   * on the opening remainder rather than on the size. Omitted is `0n`, which
   * keeps every existing BUY caller exactly as it was.
   */
  coveredShares?: bigint
  /** `BTC-USD` / `ETH-USD` — the key both caps are published under. */
  symbol: string
  /** The market's current open interest in micro-shares, or `null` if unknown. */
  openInterestShares: bigint | null

  openOrdersAccount: number
  openOrdersMarket: number

  /**
   * The funding steps still to do — `domain/trading-setup.ts`'s `missingSetup`,
   * without `SESSION_KEY`, which `gate` already carries. Omitted or empty is
   * "nothing known to be missing", which keeps every existing caller as it was.
   */
  setup?: readonly SetupStep[]
}

const READY: OrderGate = { code: 'READY', blocked: false, label: null, helper: null }

/**
 * The gate, in precedence order.
 *
 * Order is a product decision, not an implementation detail. Identity comes
 * first because a user who is not signed in cannot act on anything below it, and
 * because `ticketGate()` already owns that precedence for the header. Account
 * and venue status come next — they are facts about permission rather than about
 * this order. Transport, then the market's clock, then the order's own shape,
 * then the money. A user editing a size should see the size complaint, not a
 * balance complaint they cannot act on until the size is legal.
 */
export function validateOrder(input: OrderGateInput): OrderGate {
  return validateOrderShape(input) ?? priceGate(input) ?? riskGate(input) ?? fillNotice(input) ?? READY
}

/**
 * The prefix of the chain that does not depend on a price.
 *
 * Identity, permission, transport, the market's clock and the order's own size
 * — everything a caller can decide before knowing which price is being asked
 * about. `null` means "nothing above the price has anything to say".
 *
 * **It exists because the pro ladder has ninety-nine prices.** A click ladder
 * cannot run the whole chain per render for every rung, so it arms or disarms
 * on this prefix once and runs `validateOrder` in full on the single price a
 * click lands on. Composing it here rather than restating the prefix there is
 * what stops the two from being a second opinion about the same order.
 *
 * The first attempt at that ladder called `validateOrder` with
 * `orderType: 'LIMIT'` and `limitPriceCents: null`, which is not a
 * price-independent question at all — `priceGate` refuses a limit order with no
 * price, as it should. It returned `PRICE_TICK` with a null label, so the
 * ladder went inert with no sentence anywhere on screen saying why. A caller
 * that needs the prefix should ask for the prefix.
 */
export function validateOrderShape(input: OrderGateInput): OrderGate | null {
  return (
    identityGate(input) ??
    accountGate(input) ??
    venueGate(input) ??
    setupGate(input) ??
    transportGate(input) ??
    marketGate(input) ??
    sizeGate(input) ??
    null
  )
}

// ---------------------------------------------------------------------------

/**
 * Gates whose button **is** the way out of them.
 *
 * `blocked` answers one question — may an order be placed — and for these five
 * the answer is no and stays no. It is not the same question as *may the button
 * be pressed*, and the ticket had been using it for both: `onPrimary` routes
 * `CONNECT` to `wallet.connect()` and `ENABLE_TRADING` to
 * `wallet.promptEnableTrading()` under a comment reading "the three identity
 * gates are actions, not dead ends", while the button above it was rendered
 * `disabled` and the click never arrived. A user who dismissed the arming
 * prompt — it fires once per ticket, on first focus — had no way back to it
 * from the ticket at all.
 *
 * `docs/07` §1 described these as disabled states and now marks them enabled,
 * the way it already marks the two sell rows.
 *
 * **They are also not directional.** The primary button is `up`/`down` tinted
 * because it places a trade; `CONNECT WALLET` is not a trade, and it must not
 * be red because the DOWN toggle happens to be selected.
 */
export function isIdentityAction(code: GateCode): boolean {
  return (
    code === 'CONNECT' ||
    code === 'SIGN_IN' ||
    code === 'WRONG_ACCOUNT' ||
    code === 'WRONG_CHAIN' ||
    code === 'ENABLE_TRADING'
  )
}

function identityGate(input: OrderGateInput): OrderGate | null {
  switch (input.gate) {
    case 'CONNECT':
      return { code: 'CONNECT', blocked: true, label: 'CONNECT WALLET', helper: null }
    case 'SIGN_IN':
      return {
        code: 'SIGN_IN',
        blocked: true,
        label: 'SIGN IN',
        helper: 'One signature. No gas, no transaction.',
      }
    case 'WRONG_ACCOUNT':
      return {
        code: 'WRONG_ACCOUNT',
        blocked: true,
        label: 'SIGN IN AGAIN',
        helper:
          'Your wallet switched accounts. Sign in again to trade as the account it is on now.',
      }
    case 'WRONG_CHAIN':
      // One click switches the network and then asks for the session-key
      // signature. The wallet refuses that signature on any other network.
      return {
        code: 'WRONG_CHAIN',
        blocked: true,
        label: 'SWITCH NETWORK',
        helper: 'Your wallet is on another network. One click switches it, then enables trading.',
      }
    case 'ENABLE_TRADING':
      return {
        code: 'ENABLE_TRADING',
        blocked: true,
        label: 'ENABLE TRADING',
        helper: `One signature enables ${formatTtl(input.sessionKeyTtlMs)} of one-click trading.`,
      }
    case 'READY':
      return null
  }
}

/**
 * A funding step the ticket can walk the user through — the same
 * `ENABLE TRADING` button, opening the same setup modal, as the session key.
 *
 * Below the account and venue gates, so a restricted account or a halted venue
 * is told that rather than invited to add money it cannot trade with; above the
 * size gate, so a user with an empty ticket sees what to do before they type.
 */
function setupGate(input: OrderGateInput): OrderGate | null {
  const next = input.setup?.find((step) => step !== 'SESSION_KEY')
  if (next === undefined) return null
  return { code: 'ENABLE_TRADING', blocked: true, label: 'ENABLE TRADING', helper: SETUP_HELPER[next] }
}

const SETUP_HELPER: Record<Exclude<SetupStep, 'SESSION_KEY'>, string> = {
  // Not "No ETH needed": that is true only where the venue relays, and the
  // ticket does not know. The setup modal says which, once it opens.
  ADD_FUNDS: 'Add funds from your wallet to start trading.',
}

function accountGate(input: OrderGateInput): OrderGate | null {
  if (input.geoBlocked) {
    return {
      code: 'GEO_BLOCKED',
      blocked: true,
      label: 'NOT AVAILABLE',
      helper:
        "Trading isn't available in your location. Your USDC is in your own wallet and your open positions will still settle.",
    }
  }
  /**
   * `account_status` is **two states since §F28n**, not four.
   *
   * lint-banned-allow: naming the retired states is the point of the paragraph
   * `TRADE_ONLY`, `WITHDRAW_ONLY` and `FROZEN` were distinctions that only exist
   * when the venue holds the funds. It does not: a restriction can refuse to
   * match new orders and that is all it can do, and it deliberately does **not**
   * stop open positions settling (`docs/04` §3.2.4) — those are complete sets
   * already minted against collateral that is no longer anyone's to withhold.
   *
   * So the copy says what a restricted user actually needs to know, which is
   * the *stronger* half: their money never left their wallet.
   */
  if (input.accountStatus === 'RESTRICTED') {
    return {
      code: 'ACCOUNT_RESTRICTED',
      blocked: true,
      label: 'TRADING RESTRICTED',
      helper: "Your account can't open new orders. Your open positions will still settle.",
      action: { kind: 'SUPPORT' },
    }
  }
  return null
}

function venueGate(input: OrderGateInput): OrderGate | null {
  const { mode, estimatedResumeMs } = input.limits
  switch (mode) {
    case 'POST_ONLY':
      return {
        code: 'VENUE_POST_ONLY',
        blocked: true,
        label: 'POST ONLY',
        helper: 'The venue is accepting resting orders only.',
      }
    case 'CANCEL_ONLY':
      return {
        code: 'VENUE_CANCEL_ONLY',
        blocked: true,
        label: 'CANCEL ONLY',
        helper:
          'New orders are paused. You can still cancel, and your USDC is in your own wallet either way.',
      }
    case 'HALTED':
      return {
        code: 'VENUE_HALTED',
        blocked: true,
        label: 'TRADING HALTED',
        helper: `Trading is halted. ${resumeText(estimatedResumeMs, input.nowMs)}`,
      }
    case 'MAINTENANCE':
      return {
        code: 'VENUE_MAINTENANCE',
        blocked: true,
        label: 'MAINTENANCE',
        helper: `Back at about ${resumeText(estimatedResumeMs, input.nowMs)}`,
      }
    case 'NORMAL':
      // The heartbeat's maintenance, beneath `/config`'s own mode: it says no
      // more than the banner does, and promises no time (`docs/07` §3).
      if (input.venueMaintenance) {
        return {
          code: 'VENUE_MAINTENANCE',
          blocked: true,
          label: 'MAINTENANCE',
          helper: 'The exchange is under maintenance. Your open positions will still settle.',
        }
      }
      return null
  }
}

function transportGate(input: OrderGateInput): OrderGate | null {
  const allowance = input.rateAllowance
  if (allowance && allowance.remaining <= 0) {
    // §9.2.10. **A rejection the UI could have predicted is a UI bug** — and on
    // a 60-second market a rejected order in the last second is a lost market,
    // not an inconvenience. The allowance is the venue's own count off the
    // previous response's `RateLimit-*` headers; nothing here models the bucket.
    const seconds = allowance.resetAtMs === null ? null : (allowance.resetAtMs - input.nowMs) / 1000
    const wait = seconds === null ? null : Math.max(1, Math.ceil(seconds))
    return {
      code: 'RATE_LIMITED',
      blocked: true,
      label: wait === null ? 'SLOW DOWN' : `SLOW DOWN — ${wait}s`,
      helper: 'You have used this second’s order allowance. The button returns on its own.',
    }
  }
  if (input.signingBlocked) {
    return {
      code: 'SIGNING_UNTRUSTED',
      blocked: true,
      label: 'UNAVAILABLE',
      // The venue's own sentence, rendered as-is. There is no generic wording
      // that would help here: which member is missing, or which contract
      // disagrees with which, is the whole of what a person can act on.
      helper: input.signingBlocked.detail,
    }
  }
  if (input.limits.orderExpirationGraceMs === null) {
    // The signed `expiration` is the market's expiry plus this, and the venue
    // rebuilds it from its own number — so signing on a guess is a rejection
    // with no diagnostic. Same refusal, same reason: reconnecting fixes nothing.
    return {
      code: 'SIGNING_UNTRUSTED',
      blocked: true,
      label: 'UNAVAILABLE',
      helper:
        'The venue did not say how long a signed order stays valid on chain (config.eip712.order_expiration_grace_ms), so no order can be signed.',
    }
  }
  if (input.socketStale) {
    return {
      code: 'SOCKET_STALE',
      blocked: true,
      label: 'RECONNECTING',
      helper: 'Order entry is paused while we re-sync the book.',
    }
  }
  // D9's soft-disable, and it is deliberately narrow: only inside the final
  // 500 ms, and only when the measured offset could put the order on the wrong
  // side of the boundary. Outside that window a skewed clock costs a slightly
  // wrong countdown, which is not worth taking the button away for.
  const remainingMs = input.expiryAtMs - input.nowMs
  const offset = input.clockOffsetMs === null ? 0 : Math.abs(input.clockOffsetMs)
  if (remainingMs > 0 && remainingMs <= 500 && offset > 250) {
    return {
      code: 'CLOCK_RESYNC',
      blocked: true,
      label: 'CLOCK RE-SYNCING',
      helper: `Your clock is ${Math.round(offset)}ms off. One-click entry returns in a moment.`,
    }
  }
  return null
}

function marketGate(input: OrderGateInput): OrderGate | null {
  const { marketState, nowMs, openAtMs, expiryAtMs } = input

  if (marketState === 'SCHEDULED') {
    return {
      code: 'MARKET_SCHEDULED',
      blocked: true,
      label: `OPENS IN ${formatMmSs(Math.max(0, openAtMs - nowMs))}`,
      helper: "This market hasn't started yet.",
    }
  }
  if (marketState === 'FIXING') {
    return {
      code: 'MARKET_FIXING',
      blocked: true,
      label: `STRIKE FORMING · ${seconds(openAtMs - nowMs)}s`,
      helper: 'Trading opens when the strike is fixed.',
    }
  }
  if (marketState !== 'OPEN') {
    // Everything past `OPEN` — `EXPIRED`, `RESOLVING`, `RESOLVED`, `SETTLED`,
    // `VOIDED`, `REFUNDED`, `CANCELLED`.
    //
    // **Since register §F24 this is not the roll's happy path.** The successor
    // opens in the instant this market expires, so the ticket re-targets it and
    // stays enabled; a ticket sitting on a resolving market means a deep link
    // into an expired one, or a re-target that has not landed. The copy no
    // longer counts toward the next market's open, because it has already
    // happened — `docs/07` §4 removed that string.
    return {
      code: 'MARKET_RESOLVING',
      blocked: true,
      label: 'RESOLVING',
      helper: 'Settling now.',
    }
  }

  // §7.4. A market order received inside the last `taker_delay_ms` is accepted
  // and then terminates `EXPIRED / TAKER_DELAY_CROSSED_EXPIRY`, unfilled — so
  // the affordance is "place a limit order", which still fills. This is the one
  // place the client is ahead of the engine's gate, and it is ahead of it by
  // exactly the disclosed delay rather than by a margin of safety.
  const remainingMs = expiryAtMs - nowMs
  if (input.orderType === 'MARKET' && remainingMs > 0 && remainingMs <= input.limits.takerDelayMs) {
    return {
      code: 'TAKER_DELAY_NO_TIME',
      blocked: true,
      label: 'PLACE A LIMIT ORDER',
      helper: `Market orders need ${input.limits.takerDelayMs}ms to clear and there isn't time. A limit order still fills.`,
    }
  }
  return null
}

function sizeGate(input: OrderGateInput): OrderGate | null {
  const { sizeShares, limits } = input
  if (sizeShares <= 0n) {
    return { code: 'SIZE_EMPTY', blocked: true, label: null, helper: null }
  }
  if (sizeShares < limits.minOrderShares) {
    return {
      code: 'SIZE_BELOW_MIN',
      blocked: true,
      label: null,
      helper: `Minimum order is ${sharesText(limits.minOrderShares)} shares.`,
    }
  }
  if (sizeShares > limits.maxOrderShares) {
    return {
      code: 'SIZE_ABOVE_MAX',
      blocked: true,
      label: null,
      helper: `Maximum order is ${sharesText(limits.maxOrderShares)} shares.`,
    }
  }
  if (limits.sizeStep > 0n && sizeShares % limits.sizeStep !== 0n) {
    return {
      code: 'SIZE_OFF_STEP',
      blocked: true,
      label: null,
      helper: `Size must be a multiple of ${sharesText(limits.sizeStep)}.`,
    }
  }
  return null
}

function priceGate(input: OrderGateInput): OrderGate | null {
  const { limits, limitPriceCents, quote } = input

  if (input.orderType === 'LIMIT') {
    if (
      limitPriceCents === null ||
      !Number.isInteger(limitPriceCents) ||
      limitPriceCents < limits.minPriceCents ||
      limitPriceCents > limits.maxPriceCents
    ) {
      return {
        code: 'PRICE_TICK',
        blocked: true,
        label: null,
        helper: `Price must be a whole number of cents between ${limits.minPriceCents} and ${limits.maxPriceCents}.`,
      }
    }
    // No collar check: a limit may cross any distance through the book, and
    // fills at the resting orders' prices, never worse than its own (§F117).
  }

  // **Nothing reachable at all.** A market order is `IOC`: it takes what the
  // book has and cancels the rest, so a book that is merely *short* is not a
  // reason to refuse it — this used to block an order the venue would have
  // filled 2,000 shares of for being 9.83 shares too big. `fillNotice` says how
  // much will fill instead. `docs/07` §1 names the number.
  if (input.orderType === 'MARKET' && quote.availableShares === 0n) {
    return {
      code: 'INSUFFICIENT_LIQUIDITY',
      blocked: true,
      label: 'INSUFFICIENT LIQUIDITY',
      helper: `Only ${sharesText(quote.availableShares)} shares available at or better than ${quote.boundPriceCents ?? limits.maxPriceCents}¢.`,
    }
  }
  return null
}

function riskGate(input: OrderGateInput): OrderGate | null {
  const { limits, quote, symbol } = input
  // Only the opening part reaches a risk limit. A close returns shares that
  // were already inside every one of these caps when they were acquired.
  const openingShares = openingSharesOf(input)

  // The lock is the cost of what would fill plus the notional of what would
  // rest, and the fee reserve on top (§7.7). For a limit order that does not
  // cross, the whole size rests at the limit — so `costOf` covers both shapes
  // rather than only the marketable one.
  // A budgeted market order spends what it has (`requiredMicroUsdc`), so the
  // one balance refusal left for it is a balance that buys nothing at all —
  // and what it then "needs" is what the order would cost at the book.
  if (
    input.orderType === 'MARKET' &&
    quote.budgeted &&
    quote.shortBy === 'BALANCE' &&
    quote.filledShares === 0n &&
    input.availableMicroUsdc !== null
  ) {
    return {
      code: 'INSUFFICIENT_BALANCE',
      blocked: true,
      label: 'INSUFFICIENT BALANCE',
      helper: `You have ${formatUsdc(formatFixed(input.availableMicroUsdc, USDC_DP))} available. This order needs ${formatUsdc(formatFixed(quote.wantedMicroUsdc, USDC_DP))}.`,
    }
  }

  const required = requiredMicroUsdc(input)
  if (input.availableMicroUsdc !== null && required > input.availableMicroUsdc) {
    return {
      code: 'INSUFFICIENT_BALANCE',
      blocked: true,
      label: 'INSUFFICIENT BALANCE',
      helper: `You have ${formatUsdc(formatFixed(input.availableMicroUsdc, USDC_DP))} available. This order needs ${formatUsdc(formatFixed(required, USDC_DP))}.`,
    }
  }

  const positionCap = limits.perWalletMarketCap[symbol]
  if (positionCap !== undefined) {
    const headroom = positionCap - input.positionShares
    if (openingShares > headroom) {
      return {
        code: 'POSITION_CAP',
        blocked: true,
        label: null,
        helper: `You can hold ${sharesText(headroom > 0n ? headroom : 0n)} more shares in this market.`,
        action: { kind: 'USE_MAX', sizeShares: floorToStep(headroom, limits.sizeStep) },
      }
    }
  }

  const oiCap = limits.perMarketOpenInterestCap[symbol]
  if (oiCap !== undefined && input.openInterestShares !== null) {
    const remaining = oiCap - input.openInterestShares
    // Open interest is complete pairs, and only the minting part creates any
    // (matcher's D1b). A close destroys a pair rather than making one.
    const minting = quote.filledShares < openingShares ? quote.filledShares : openingShares
    if (minting > remaining) {
      // "It's a safety cap, not a fault" (§4.3, docs/07 rule 3). Never a red
      // failure, and never phrased as something the user did.
      return {
        code: 'MARKET_OI_CAP',
        blocked: true,
        label: 'MARKET AT CAPACITY',
        helper: `${sharesText(remaining > 0n ? remaining : 0n)} shares left before this market's open-interest cap.`,
      }
    }
  }

  if (input.openOrdersAccount >= limits.maxOpenOrdersPerAccount) {
    return {
      code: 'MAX_OPEN_ORDERS',
      blocked: true,
      label: null,
      helper: `You have ${input.openOrdersAccount} open orders. Cancel one to place another.`,
    }
  }
  if (input.openOrdersMarket >= limits.maxOpenOrdersPerMarket) {
    return {
      code: 'MAX_OPEN_ORDERS',
      blocked: true,
      label: null,
      helper: `You have ${input.openOrdersMarket} open orders in this market — the limit is ${limits.maxOpenOrdersPerMarket}.`,
    }
  }
  return null
}

/**
 * **Not a block**: a market order that will fill only part of its size, and why.
 *
 * Last in the chain, because it is a note about an order that may be placed —
 * any reason it may not wins. The ticket renders the helper under an enabled
 * button. Either the book holds fewer shares inside the bound than were asked
 * for, or the balance runs out first (`docs/04` §7.7); the venue fills what it
 * can and cancels the rest, and this says so before the click.
 */
function fillNotice(input: OrderGateInput): OrderGate | null {
  const { quote, sizeShares } = input
  if (input.orderType !== 'MARKET' || quote.filledShares <= 0n || quote.filledShares >= sizeShares) {
    return null
  }
  const filled = sharesText(quote.filledShares)
  const size = sharesText(sizeShares)
  return {
    code: 'PARTIAL_FILL',
    blocked: false,
    label: null,
    helper:
      quote.shortBy === 'BALANCE' ?
        `Your balance covers ${filled} of ${size} shares at these prices. The rest is cancelled.`
      : `Only ${filled} of ${size} shares can fill right now. The rest is cancelled.`,
  }
}

// ---------------------------------------------------------------------------

/**
 * What the engine would lock (§7.7): notional for every share, plus the
 * projected taker fee on the marketable part.
 *
 * The unfilled remainder of a limit order rests at the limit and is locked at
 * the limit, so it is counted at `limitPriceCents` rather than dropped — a
 * balance check that only priced the crossing part would tell a user they can
 * afford an order the engine then refuses.
 */
export function requiredMicroUsdc(input: OrderGateInput): bigint {
  const { quote, sizeShares, limitPriceCents } = input
  const opening = openingSharesOf(input)
  if (opening <= 0n) {
    // A wholly covered sell. §7.7's first SELL row: "**0** — it is a close, the
    // shares are the collateral." Not a rounding-down of a small number; there
    // is genuinely nothing to reserve.
    return 0n
  }
  // **A market order spends what its walk spends** — `docs/04` §7.7's
  // budgeted market order (2026-09-30). The venue holds the account's money
  // rather than `size × bound` and stops where it runs out, so the order needs
  // what the quote's budgeted walk pays, which is never more than the budget.
  if (input.orderType === 'MARKET' && quote.budgeted) return quote.spentMicroUsdc
  if (opening < sizeShares) {
    /**
     * A partly covered sell, which `domain/sell-shape.ts` places as **two
     * orders** against one ladder. Which of them reaches a given level depends
     * on what else arrives in between, so a walk-derived cost here would be a
     * number this client cannot stand behind.
     *
     * Priced at the bound instead — `size × lock_price`, which is exactly what
     * §7.7 says the engine reserves for the uncovered part and is never an
     * under-estimate for a marketable order. A balance check that under-states
     * tells a user they can afford an order the engine then refuses, which is
     * the one direction this must not fail in.
     */
    const bound = limitPriceCents ?? quote.boundPriceCents
    if (bound === null) return 0n
    return ceil100(opening * BigInt(bound)) + quote.feeReserveMicroUsdc
  }
  const resting = sizeShares - quote.filledShares
  const restingCost =
    resting > 0n && limitPriceCents !== null
      ? ceil100(resting * BigInt(limitPriceCents))
      : 0n
  return quote.costMicroUsdc + restingCost + quote.feeReserveMicroUsdc
}

/** `sizeShares` less the part that closes an existing position (§7.7). */
function openingSharesOf(input: OrderGateInput): bigint {
  const covered = input.coveredShares ?? 0n
  const opening = input.sizeShares - covered
  return opening > 0n ? opening : 0n
}

function ceil100(hundredths: bigint): bigint {
  const q = hundredths / 100n
  return hundredths % 100n > 0n ? q + 1n : q
}

/** The largest legal size at or below `shares`. `0n` when nothing fits. */
export function floorToStep(shares: bigint, step: bigint): bigint {
  if (shares <= 0n || step <= 0n) return 0n
  return (shares / step) * step
}

function sharesText(micro: bigint): string {
  return formatShares(formatFixed(micro, SHARES_DP))
}

function seconds(ms: number): number {
  return Math.max(0, Math.ceil(ms / 1000))
}

function resumeText(estimatedResumeMs: number | null, nowMs: number): string {
  if (estimatedResumeMs === null) return "We'll post an update shortly."
  const minutes = Math.max(1, Math.round((estimatedResumeMs - nowMs) / 60_000))
  return `About ${minutes} minute${minutes === 1 ? '' : 's'}.`
}
